// appointment.action.ts
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  AppointmentRequest,
  AppointmentResponse,
} from '@dataTypes/appointment.type'
import {
  createAppointment,
  deleteAppointmentById,
  getAllAppointments,
  updateAppointmentById,
  updateAppointmentStatusById,
} from '@services/api/appointment.api'
import { calculateDateRange } from '@utils/time.util'
import { refreshFromPatient } from '@store/reducers/patientmanagement.reducer'

export const fetchAllAppointments = createAsyncThunk<
  AppointmentResponse[],
  { startDate: string | null; endDate: string | null },
  {
    rejectValue: string
  }
>(
  'api/appointments/fetch',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    // Always try to fetch real data first - real data takes priority
    try {
      const response = await getAllAppointments(startDate, endDate)

      // Check if we got valid real data
      if (
        response.statusCode === 200 &&
        response.data &&
        'appointments' in response.data &&
        Array.isArray(response.data.appointments)
      ) {
        const appointments = response.data.appointments as AppointmentResponse[]

        // If we have real appointments, return ONLY real data (never mix with mock)
        if (appointments.length > 0) {
          return appointments
        }

        return []
      }

      return rejectWithValue(response.message || 'Failed to fetch appointments')
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const addAppointment = createAsyncThunk<
  void,
  {
    appointmentData: Partial<AppointmentRequest>
    date: string | Date
    activeTab: string
  },
  {
    rejectValue: string
  }
>(
  'appointments/create',
  async (
    { appointmentData, date, activeTab },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await createAppointment(appointmentData)

      if (response.statusCode !== 201) {
        return rejectWithValue(
          response.message || 'Failed to fetch appointments',
        )
      }

      const { startDate, endDate } = calculateDateRange(activeTab, date)
      dispatch(fetchAllAppointments({ startDate, endDate }))
      dispatch(refreshFromPatient())
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create appointment')
    }
  },
)

export const updateAppointment = createAsyncThunk<
  AppointmentResponse,
  {
    appointment_id: string
    updates: Partial<AppointmentRequest>
    updateByDragAndDrop?: boolean
  },
  { rejectValue: string }
>(
  'api/appointments/update',
  async (
    { appointment_id, updates, updateByDragAndDrop = false },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await updateAppointmentById(
        appointment_id,
        updates,
        updateByDragAndDrop,
      )

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'appointment' in response.data &&
        typeof response.data.appointment === 'object'
      ) {
        dispatch(refreshFromPatient())
        return response.data.appointment as AppointmentResponse
      }

      return rejectWithValue('Appointments not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)

export const deleteAppointment = createAsyncThunk<
  string,
  string,
  {
    rejectValue: string
  }
>('api/appointments/delete', async (id, { dispatch, rejectWithValue }) => {
  try {
    const response = await deleteAppointmentById(id)
    if (response.statusCode !== 200) {
      throw new Error(response.message)
    }
    dispatch(refreshFromPatient())
    return id
  } catch (e: unknown) {
    if (e instanceof Error) {
      return rejectWithValue(e.message)
    }
    return rejectWithValue('An unknown error occurred')
  }
})

export const updateAppointmentStatus = createAsyncThunk<
  AppointmentResponse,
  { appointment_id: string; status: number },
  { rejectValue: string }
>(
  'api/appointments/updatestatus',
  async ({ appointment_id, status }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateAppointmentStatusById(appointment_id, status)

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'appointment' in response.data &&
        typeof response.data.appointment === 'object'
      ) {
        dispatch(refreshFromPatient())
        return response.data.appointment as AppointmentResponse
      }

      return rejectWithValue('Appointments not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)
